import type { PDFDocument, PDFPage, PDFFont, PDFImage } from 'pdf-lib';
import { rgb } from 'pdf-lib';
import type { ReposicionPdfDocument, ReposicionPdfImage } from '../../domain/ReposicionPdfModel.ts';
import {
  REPOSICION_PDF_DOC_ID,
  REPOSICION_PDF_DOC_ID_SIZE,
  REPOSICION_PDF_FIRMA_BOX,
  REPOSICION_PDF_FOTO_BOX,
  REPOSICION_PDF_PAGE1_POSITIONS,
  REPOSICION_PDF_PAGE2_POSITIONS,
  REPOSICION_PDF_TEMPLATE_PAGE,
  REPOSICION_PDF_FONT_SIZE,
} from './reposicionPdfTemplateLayout.ts';

async function embedPdfImage(doc: PDFDocument, image: ReposicionPdfImage): Promise<PDFImage | null> {
  if (!image.dataUrl) return null;
  try {
    const raw = image.dataUrl.split(',')[1];
    if (!raw) return null;
    const bytes = Uint8Array.from(atob(raw), (c) => c.charCodeAt(0));
    return image.format === 'PNG' ? doc.embedPng(bytes) : doc.embedJpg(bytes);
  } catch {
    return null;
  }
}

function wrapLine(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const out: string[] = [];
  let line = words[0] ?? '';
  for (let i = 1; i < words.length; i++) {
    const next = `${line} ${words[i]}`;
    if (font.widthOfTextAtSize(next, size) <= maxWidth) line = next;
    else {
      out.push(line);
      line = words[i] ?? '';
    }
  }
  out.push(line);
  return out;
}

function drawLines(
  page: PDFPage,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  font: PDFFont,
  size: number,
): void {
  const lines = text.split('\n').flatMap((part) => wrapLine(part, font, size, maxWidth));
  let cy = y;
  for (const line of lines) {
    page.drawText(line, { x, y: cy, size, font, color: rgb(0, 0, 0) });
    cy -= size * 1.25;
  }
}

function page1Values(model: ReposicionPdfDocument): Record<string, string> {
  return {
    idReposicion: model.idReposicion,
    fecha: model.fecha,
    documentoBeneficiario: model.documentoBeneficiario,
    nombreBeneficiario: model.nombreBeneficiario,
    comuna: model.comuna,
    direccion: model.direccion,
    productosAReponer: model.productosAReponer,
    ...model.cantidades,
    medioReporte: model.medioReporte,
  };
}

function page2Values(model: ReposicionPdfDocument): Record<string, string> {
  return {
    otroMedioReporte: model.otroMedioReporte,
    motivoReposicion: model.motivoReposicion,
    otroMotivoReposicion: model.otroMotivoReposicion,
    recibeOtraPersona: model.recibeOtraPersona,
    parentesco: model.parentesco,
    documentoAcudiente: model.documentoAcudiente,
    nombreRecibe: model.nombreRecibe,
    responsableReposicion: model.responsableReposicion,
  };
}

function drawFieldValues(
  page: PDFPage,
  fields: { key: string; x: number; y: number; maxWidth: number }[],
  values: Record<string, string>,
  font: PDFFont,
): void {
  for (const field of fields) {
    const text = values[field.key];
    if (text == null || text === '') continue;
    drawLines(page, text, field.x, field.y, field.maxWidth, font, REPOSICION_PDF_FONT_SIZE);
  }
}

async function drawDatosPage(
  outDoc: PDFDocument,
  templateDoc: PDFDocument,
  model: ReposicionPdfDocument,
  font: PDFFont,
): Promise<void> {
  const [tpl] = await outDoc.copyPages(templateDoc, [REPOSICION_PDF_TEMPLATE_PAGE.datos]);
  const page = outDoc.addPage(tpl);
  drawFieldValues(page, REPOSICION_PDF_PAGE1_POSITIONS, page1Values(model), font);
}

async function drawAcudientePage(
  outDoc: PDFDocument,
  templateDoc: PDFDocument,
  model: ReposicionPdfDocument,
  font: PDFFont,
): Promise<void> {
  const [tpl] = await outDoc.copyPages(templateDoc, [REPOSICION_PDF_TEMPLATE_PAGE.acudiente]);
  const page = outDoc.addPage(tpl);
  drawFieldValues(page, REPOSICION_PDF_PAGE2_POSITIONS, page2Values(model), font);

  const firma = await embedPdfImage(outDoc, model.firma);
  if (firma) page.drawImage(firma, REPOSICION_PDF_FIRMA_BOX);

  const foto = await embedPdfImage(outDoc, model.foto);
  if (foto) page.drawImage(foto, REPOSICION_PDF_FOTO_BOX);

  page.drawText(model.documentoId, {
    x: REPOSICION_PDF_DOC_ID.x,
    y: REPOSICION_PDF_DOC_ID.y,
    size: REPOSICION_PDF_DOC_ID_SIZE,
    font,
  });
}

export async function renderReposicionPdfFromTemplate(
  templateBytes: Uint8Array,
  model: ReposicionPdfDocument,
): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts } = await import('pdf-lib');
  const templateDoc = await PDFDocument.load(templateBytes);
  const outDoc = await PDFDocument.create();
  const font = await outDoc.embedFont(StandardFonts.Helvetica);

  await drawDatosPage(outDoc, templateDoc, model, font);
  await drawAcudientePage(outDoc, templateDoc, model, font);

  return outDoc.save();
}
